import type {Mapper} from '@automapper/core';
import type {DownloadColumn, IDownloadByLinkService} from '@igp/shared/api';
import {ServerResponseStatus} from '@igp/shared/api';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {debtResponsibleMasterAgentMappingKeys} from './mapper';
import type {DebtResponsibleMasterAgentsService} from './service';
import {
    type DebtResponsibleMasterAgentDownloadFields,
    DebtResponsibleMasterAgentsServiceTypes,
    type DebtResponsibleMasterAgentViewModelKeys,
} from './types';

@injectable()
export class DebtResponsibleMasterAgentDownloadClientStrategy extends EntityDownloadClientStrategy<
    DebtResponsibleMasterAgentViewModelKeys,
    EntityType
> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(DebtResponsibleMasterAgentsServiceTypes.service) service: DebtResponsibleMasterAgentsService
    ) {
        super(mapper, service, EntityType.DebtResponsibleMasterAgents, {
            viewModelKeys: debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            queryFields: debtResponsibleMasterAgentMappingKeys.queryFields,
            model: debtResponsibleMasterAgentMappingKeys.serverModel,
            downloadModel: debtResponsibleMasterAgentMappingKeys.downloadModel,
        });
    }
}

@injectable()
export class DebtResponsibleMasterAgentDownloadServerStrategy implements IDownloadStrategy<DebtResponsibleMasterAgentViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<DebtResponsibleMasterAgentDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(DebtResponsibleMasterAgentsServiceTypes.service)
        service: IDownloadByLinkService<DebtResponsibleMasterAgentDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<DebtResponsibleMasterAgentViewModelKeys>
    ): Observable<DownloadStrategyResponse<DebtResponsibleMasterAgentViewModelKeys>> {
        const columns: DownloadColumn<DebtResponsibleMasterAgentDownloadFields>[] = request.keys?.map<
            DownloadColumn<DebtResponsibleMasterAgentDownloadFields>
        >(k => {
            const queryField = this._mapper.map<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentDownloadFields>(
                k,
                debtResponsibleMasterAgentMappingKeys.viewModelKeys,
                debtResponsibleMasterAgentMappingKeys.downloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
